/**
 * /dev — Component playground. NOT linked from the app; local only.
 *
 * RULE: Every new component must be added here with at least one rendered
 * example. This makes it easy to visually verify components in isolation
 * without navigating the full app.
 */
import { useState } from "react";
import { useLocation } from "wouter";
import { DirArrow, SmallArrow } from "../components/Arrow";
import { DaySelector } from "../components/DaySelector";
import { DetailPageSpotHeader } from "../components/DetailPageSpotHeader";
import { Footer } from "../components/Footer";
import { ForecastCol } from "../components/ForecastCol";
import { HourlyChart } from "../components/HourlyChart";
import { HourlyForecastCard } from "../components/HourlyForecastCard";
import { Icon } from "../components/Icon";
import { IconButton } from "../components/IconButton";
import { ListItem } from "../components/ListItem";
import { Logo } from "../components/Logo";
import { Navbar } from "../components/Navbar";
import { SearchModal } from "../components/SearchModal";
import { SkeletonSpotCard, SpotCard } from "../components/SpotCard";
import { SpotDescription } from "../components/SpotDescription";
import { Tag } from "../components/Tag";
import { TextInput } from "../components/TextInput";
import { UnitButton } from "../components/UnitButton";
import { useUnit } from "../lib/hooks";
import type { Band, EnrichedSpot, ScoredDay, ScoredHour, SpeedUnit } from "../lib/types";

// ---------------------------------------------------------------------------
// Mock data
// ---------------------------------------------------------------------------

const TODAY = new Date();
function isoDate(offset: number) {
  const d = new Date(TODAY);
  d.setDate(d.getDate() + offset);
  return d.toISOString().slice(0, 10);
}
function isoHour(offsetDay: number, hour: number) {
  return `${isoDate(offsetDay)}T${String(hour).padStart(2, "0")}:00:00`;
}

function makeHour(
  dayOffset: number,
  hour: number,
  speedMs: number,
  gustMs: number,
  dirDeg: number,
  isNow = false,
): ScoredHour {
  return {
    time: isoHour(dayOffset, hour),
    speedMs,
    gustMs,
    dirDeg,
    tempC: 18,
    isNow,
    score: 80,
    level: "prime",
  };
}

function makeBand(meanSpeedMs: number): Band {
  return { meanSpeedMs, score: 80, level: "prime" };
}

const MOCK_HOURS_D0: ScoredHour[] = [
  makeHour(0, 6, 3.2, 4.1, 220),
  makeHour(0, 7, 4.5, 5.8, 225),
  makeHour(0, 8, 6.0, 7.5, 230),
  makeHour(0, 9, 7.2, 9.0, 235, true),
  makeHour(0, 10, 8.5, 10.5, 240),
  makeHour(0, 11, 9.1, 11.2, 238),
  makeHour(0, 12, 10.3, 12.8, 235),
  makeHour(0, 13, 11.0, 13.5, 230),
  makeHour(0, 14, 10.5, 13.0, 228),
  makeHour(0, 15, 9.8, 12.0, 225),
  makeHour(0, 16, 8.5, 11.0, 220),
  makeHour(0, 17, 7.5, 9.5, 215),
  makeHour(0, 18, 6.0, 7.8, 210),
  makeHour(0, 19, 4.5, 5.9, 205),
  makeHour(0, 20, 3.0, 4.1, 200),
];

const MOCK_HOURS_D1: ScoredHour[] = [
  makeHour(1, 6, 5.0, 6.5, 180),
  makeHour(1, 7, 5.8, 7.2, 185),
  makeHour(1, 8, 6.5, 8.0, 190),
  makeHour(1, 9, 7.0, 8.8, 195),
  makeHour(1, 10, 6.8, 8.5, 192),
  makeHour(1, 11, 6.2, 7.9, 188),
  makeHour(1, 12, 5.5, 7.0, 185),
  makeHour(1, 13, 4.8, 6.1, 180),
  makeHour(1, 14, 4.2, 5.5, 175),
  makeHour(1, 15, 3.8, 5.0, 170),
  makeHour(1, 16, 3.2, 4.2, 168),
  makeHour(1, 17, 2.8, 3.8, 165),
];

const MOCK_HOURS_D2: ScoredHour[] = [
  makeHour(2, 8, 1.5, 2.0, 90),
  makeHour(2, 9, 2.0, 2.8, 95),
  makeHour(2, 10, 2.5, 3.2, 100),
  makeHour(2, 11, 2.2, 3.0, 98),
  makeHour(2, 12, 1.8, 2.5, 95),
  makeHour(2, 13, 1.5, 2.0, 92),
  makeHour(2, 14, 1.2, 1.8, 90),
];

const MOCK_DAYS: ScoredDay[] = [
  {
    date: isoDate(0),
    dayOffset: 0,
    level: "prime",
    score: 90,
    matchType: "good",
    speedMinMs: 3.2,
    speedMaxMs: 11.0,
    gustMaxMs: 13.5,
    dirDeg: 232,
    dirSet: ["SW", "WSW"],
    bands: [makeBand(5.0), makeBand(9.2), makeBand(10.5), makeBand(8.0)],
    hours: MOCK_HOURS_D0,
  },
  {
    date: isoDate(1),
    dayOffset: 1,
    level: "good",
    score: 65,
    matchType: "ok",
    speedMinMs: 2.8,
    speedMaxMs: 7.0,
    gustMaxMs: 8.8,
    dirDeg: 185,
    dirSet: ["S", "SSW"],
    bands: [makeBand(4.5), makeBand(6.5), makeBand(5.0)],
    hours: MOCK_HOURS_D1,
  },
  {
    date: isoDate(2),
    dayOffset: 2,
    level: "marginal",
    score: 30,
    matchType: "bad",
    speedMinMs: 1.2,
    speedMaxMs: 2.5,
    gustMaxMs: 3.2,
    dirDeg: 95,
    dirSet: ["E", "ESE"],
    bands: [makeBand(2.0), makeBand(2.2)],
    hours: MOCK_HOURS_D2,
  },
  {
    date: isoDate(3),
    dayOffset: 3,
    level: "unlikely",
    score: 5,
    matchType: "bad",
    speedMinMs: 0,
    speedMaxMs: 0.8,
    gustMaxMs: 1.2,
    dirDeg: 10,
    dirSet: ["N"],
    bands: [],
    hours: [],
  },
  {
    date: isoDate(4),
    dayOffset: 4,
    level: "skip",
    score: 0,
    bands: [],
    hours: [],
  },
  {
    date: isoDate(5),
    dayOffset: 5,
    level: "good",
    score: 70,
    matchType: "ok",
    speedMinMs: 4.0,
    speedMaxMs: 9.0,
    gustMaxMs: 11.0,
    dirDeg: 200,
    dirSet: ["SSW", "SW"],
    bands: [makeBand(6.0), makeBand(8.0)],
    hours: [],
  },
  {
    date: isoDate(6),
    dayOffset: 6,
    level: "prime",
    score: 88,
    matchType: "good",
    speedMinMs: 6.0,
    speedMaxMs: 12.0,
    gustMaxMs: 14.0,
    dirDeg: 225,
    dirSet: ["SW", "WSW"],
    bands: [makeBand(8.0), makeBand(11.0)],
    hours: [],
  },
];

// Full-range wind-speed sweep for visualising the chart palette end-to-end.
// 3 days × 24 hours, speed climbs from ~1 kn calm overnight day 0 up to
// ~65 kn storm peak day 2 — every CHART_KNOT_STOPS band gets coverage with
// a realistic diurnal shape (dawn lull → afternoon peak → evening ease).
const SWEEP_KN_BY_HOUR: number[] = [
  // Day 0 — calm to optimal (0 → ~22 kn peak)
  1, 1, 2, 2, 3, 3, 4, 6, 8, 11, 14, 17, 20, 22, 22, 21, 18, 15, 12, 10, 8, 6, 5, 4,
  // Day 1 — optimal to strong (12 → ~38 kn peak)
  12, 13, 14, 15, 17, 19, 22, 25, 28, 31, 34, 36, 38, 38, 37, 35, 32, 29, 26, 23, 20, 18, 16, 14,
  // Day 2 — strong to storm (28 → ~66 kn peak)
  28, 30, 32, 35, 38, 42, 46, 50, 54, 58, 61, 64, 66, 66, 64, 60, 56, 51, 46, 42, 38, 35, 32, 30,
];
const SWEEP_HOURS: ScoredHour[] = SWEEP_KN_BY_HOUR.map((kn, i) => {
  const dayOffset = Math.floor(i / 24);
  const hour = i % 24;
  const speedMs = kn / 1.944;
  const gustMs = speedMs * 1.25;
  // Direction drifts a little so arrows aren't visually flat.
  const dirDeg = 200 + (i % 12) * 4;
  return makeHour(dayOffset, hour, speedMs, gustMs, dirDeg);
});
const SWEEP_DAYS: ScoredDay[] = [0, 1, 2].map((offset) => {
  const hours = SWEEP_HOURS.filter((h) => h.time.startsWith(isoDate(offset)));
  const speeds = hours.map((h) => h.speedMs);
  const gusts = hours.map((h) => h.gustMs);
  const meanMs = speeds.reduce((s, n) => s + n, 0) / speeds.length;
  return {
    date: isoDate(offset),
    dayOffset: offset,
    score: 80,
    level: "prime",
    matchType: "good",
    speedMinMs: Math.min(...speeds),
    speedMaxMs: Math.max(...speeds),
    gustMaxMs: Math.max(...gusts),
    dirDeg: 220,
    dirSet: ["SW"],
    bands: [makeBand(meanMs)],
    hours,
  };
});

const MOCK_SPOT: EnrichedSpot = {
  id: "mock-spot",
  name: "Bosön",
  region: "stockholm",
  subregion: "Stockholm",
  area: "Lidingö",
  coords: { lat: 59.37, lon: 18.15 },
  priority: 1,
  ideal: { dirs: ["SW", "WSW", "S"], speed_min: 6, speed_max: 14 },
  secondary: { dirs: ["SSW", "W"], speed_min: 5, speed_max: 16 },
  score: 90,
  matchType: "good",
  wind: MOCK_HOURS_D0[3] ?? null,
  windHours: MOCK_HOURS_D0,
  days: MOCK_DAYS,
};

const SWEEP_SPOT: EnrichedSpot = {
  ...MOCK_SPOT,
  id: "sweep-spot",
  name: "Palette sweep",
  windHours: SWEEP_HOURS,
  days: SWEEP_DAYS,
};

// ---------------------------------------------------------------------------
// Dev page layout helpers
// ---------------------------------------------------------------------------

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ marginBottom: 48 }}>
      <h2
        style={{
          fontFamily: "var(--font-body)",
          fontSize: "var(--text-caption)",
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          color: "var(--foreground-muted)",
          marginBottom: 16,
          paddingBottom: 8,
          borderBottom: "1px solid var(--border-faint)",
        }}
      >
        {title}
      </h2>
      {children}
    </section>
  );
}

function Row({ gap = 16, wrap = true, children }: { gap?: number; wrap?: boolean; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", gap, flexWrap: wrap ? "wrap" : "nowrap", alignItems: "flex-start" }}>
      {children}
    </div>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ fontSize: 11, color: "var(--foreground-muted)", marginTop: 6, textAlign: "center" }}>
      {children}
    </div>
  );
}

function Item({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
      {children}
      <Label>{label}</Label>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function DevPage() {
  const { unit, cycleUnit } = useUnit();
  const [, setLocation] = useLocation();
  const [selectedHourIdx, setSelectedHourIdx] = useState(3);
  const [selectedDay, setSelectedDay] = useState(0);
  const [scrollTick, setScrollTick] = useState(0);
  const [searchOpen, setSearchOpen] = useState(false);

  const allFlatHours = [...MOCK_HOURS_D0, ...MOCK_HOURS_D1, ...MOCK_HOURS_D2];
  const selectedHour = allFlatHours[selectedHourIdx] ?? null;

  const DIRECTIONS = [0, 45, 90, 135, 180, 225, 270, 315];
  const SPEED_UNITS: SpeedUnit[] = ["kn", "ms", "kmh", "mph"];

  return (
    <div
      style={{
        maxWidth: 900,
        margin: "0 auto",
        padding: "48px 24px",
        fontFamily: "var(--font-body)",
        color: "var(--foreground-default)",
      }}
    >
      <h1
        style={{
          fontFamily: "var(--font-display)",
          fontSize: "var(--text-headline)",
          fontWeight: 700,
          marginBottom: 8,
        }}
      >
        Component playground
      </h1>
      <p style={{ color: "var(--foreground-muted)", marginBottom: 16, fontSize: "var(--text-body)" }}>
        /dev — only used locally, not linked from the app.
      </p>
      <div style={{ display: "flex", gap: 8, marginBottom: 48, flexWrap: "wrap" }}>
        <button
          type="button"
          onClick={() => setLocation("/map-proto")}
          style={{
            padding: "0 16px",
            height: 40,
            borderRadius: "var(--radius-md)",
            border: "1px solid var(--border-default)",
            background: "var(--action-default)",
            color: "var(--foreground-default)",
            fontFamily: "var(--font-body)",
            fontSize: "var(--text-caption)",
            cursor: "pointer",
          }}
        >
          → Map prototype (Sweden grid)
        </button>
        <button
          type="button"
          onClick={() => setLocation("/spot-map-proto/rangsta")}
          style={{
            padding: "0 16px",
            height: 40,
            borderRadius: "var(--radius-md)",
            border: "1px solid var(--border-default)",
            background: "var(--action-default)",
            color: "var(--foreground-default)",
            fontFamily: "var(--font-body)",
            fontSize: "var(--text-caption)",
            cursor: "pointer",
          }}
        >
          → Spot detail map (uniform wind)
        </button>
        <button
          type="button"
          onClick={() => setLocation("/dev/social")}
          style={{
            padding: "0 16px",
            height: 40,
            borderRadius: "var(--radius-md)",
            border: "1px solid var(--border-default)",
            background: "var(--action-default)",
            color: "var(--foreground-default)",
            fontFamily: "var(--font-body)",
            fontSize: "var(--text-caption)",
            cursor: "pointer",
          }}
        >
          → Social export studio (IG carousels)
        </button>
        <button
          type="button"
          onClick={() => setLocation("/particles-tune")}
          style={{
            padding: "0 16px",
            height: 40,
            borderRadius: "var(--radius-md)",
            border: "1px solid var(--border-default)",
            background: "var(--action-default)",
            color: "var(--foreground-default)",
            fontFamily: "var(--font-body)",
            fontSize: "var(--text-caption)",
            cursor: "pointer",
          }}
        >
          → Particles tuning (6 maps)
        </button>
      </div>

      {/* ------------------------------------------------------------------ */}
      <Section title="Logo">
        <Row>
          <Item label="default">
            <Logo />
          </Item>
        </Row>
      </Section>

      {/* ------------------------------------------------------------------ */}
      {/* Icon + IconButton (40×40, two variants: outline + ghost). */}
      <Section title="Icon + IconButton">
        <Row>
          <Item label="chevron-left">
            <Icon name="chevron-left" />
          </Item>
          <Item label="chevron-right">
            <Icon name="chevron-right" />
          </Item>
          <Item label="arrow-left">
            <Icon name="arrow-left" />
          </Item>
        </Row>
        <div style={{ marginTop: 16 }}>
          <Label>outline</Label>
          <Row>
            <Item label="default">
              <IconButton aria-label="go left">
                <Icon name="chevron-left" />
              </IconButton>
            </Item>
            <Item label="default">
              <IconButton aria-label="go right">
                <Icon name="chevron-right" />
              </IconButton>
            </Item>
            <Item label="disabled">
              <IconButton disabled aria-label="disabled example">
                <Icon name="chevron-right" />
              </IconButton>
            </Item>
          </Row>
        </div>
        <div style={{ marginTop: 16 }}>
          <Label>ghost</Label>
          <Row>
            <Item label="default">
              <IconButton variant="ghost" aria-label="back">
                <Icon name="arrow-left" />
              </IconButton>
            </Item>
            <Item label="disabled">
              <IconButton variant="ghost" disabled aria-label="disabled example">
                <Icon name="arrow-left" />
              </IconButton>
            </Item>
          </Row>
        </div>
        <div
          style={{
            marginTop: 16,
            padding: 16,
            background: "var(--surface-raised)",
            borderRadius: "var(--radius-md)",
          }}
        >
          <Label>elevated — sits on a surface-raised parent</Label>
          <Row>
            <Item label="default">
              <IconButton variant="elevated" aria-label="close">
                <Icon name="close" />
              </IconButton>
            </Item>
            <Item label="disabled">
              <IconButton variant="elevated" disabled aria-label="disabled example">
                <Icon name="close" />
              </IconButton>
            </Item>
          </Row>
        </div>
      </Section>

      {/* ------------------------------------------------------------------ */}
      <Section title="Navbar">
        <Label>variant=region</Label>
        <Navbar
          variant="region"
          unit={unit}
          onCycleUnit={cycleUnit}
          onOpenSearch={() => {}}
        />
        <div style={{ marginTop: 32 }}>
          <Label>variant=spots</Label>
          <Navbar
            variant="spots"
            title="Stockholm"
            onBack={() => {}}
            unit={unit}
            onCycleUnit={cycleUnit}
            onOpenSearch={() => {}}
          />
        </div>
        <div style={{ marginTop: 32 }}>
          <Label>variant=spots — long region name</Label>
          <Navbar
            variant="spots"
            title="Västra Götaland"
            onBack={() => {}}
            unit={unit}
            onCycleUnit={cycleUnit}
            onOpenSearch={() => {}}
          />
        </div>
        <div style={{ marginTop: 32 }}>
          <Label>variant=detail</Label>
          <Navbar
            variant="detail"
            onBack={() => {}}
            unit={unit}
            onCycleUnit={cycleUnit}
          />
        </div>
      </Section>

      {/* ------------------------------------------------------------------ */}
      <Section title="TextInput">
        <Row gap={16}>
          <Item label="default">
            <TextInput
              label="Spot name"
              placeholder="e.g. Torö Stenstrand"
            />
          </Item>
          <Item label="with hint">
            <TextInput
              label="Your email"
              placeholder="you@example.com"
              hint="Optional — only if you want a reply."
            />
          </Item>
          <Item label="error">
            <TextInput
              label="Your email"
              placeholder="you@example.com"
              defaultValue="not-an-email"
              error
              hint="Doesn't look like a valid email."
            />
          </Item>
          <Item label="multiline">
            <TextInput
              multiline
              label="Notes"
              rows={4}
              placeholder="e.g. Flat to choppy bay, rocky point 30 m off the launch, clear once you're past it. Free roadside parking, no facilities."
            />
          </Item>
        </Row>
      </Section>

      {/* ------------------------------------------------------------------ */}
      <Section title="SearchModal">
        <p style={{ fontSize: 12, color: "var(--foreground-muted)", marginBottom: 12 }}>
          Click to open. Escape or X closes. Try "torö", "stockholm", or "kite".
        </p>
        <button
          type="button"
          onClick={() => setSearchOpen(true)}
          style={{
            padding: "0 16px",
            height: 40,
            borderRadius: "var(--radius-md)",
            border: "1px solid var(--border-default)",
            background: "var(--action-default)",
            color: "var(--foreground-default)",
          }}
        >
          Open search
        </button>
        <SearchModal
          open={searchOpen}
          onClose={() => setSearchOpen(false)}
          spots={[MOCK_SPOT]}
          unit={unit}
        />
      </Section>

      {/* ------------------------------------------------------------------ */}
      <Section title="Arrows">
        <Row>
          {DIRECTIONS.map((deg) => (
            <Item key={deg} label={`${deg}°`}>
              <DirArrow dirDeg={deg} />
            </Item>
          ))}
        </Row>
        <div style={{ marginTop: 16 }}>
          <Row>
            {DIRECTIONS.map((deg) => (
              <Item key={deg} label={`${deg}°`}>
                <SmallArrow dirDeg={deg} />
              </Item>
            ))}
          </Row>
        </div>
      </Section>

      {/* ------------------------------------------------------------------ */}
      <Section title="UnitButton">
        <Row>
          {SPEED_UNITS.map((u) => (
            <Item key={u} label={u}>
              <UnitButton unit={u} onClick={cycleUnit} />
            </Item>
          ))}
        </Row>
        <p style={{ marginTop: 12, fontSize: 12, color: "var(--foreground-muted)" }}>
          Active unit: <strong>{unit}</strong> — click any button above to cycle.
        </p>
      </Section>

      {/* ------------------------------------------------------------------ */}
      <Section title="Tag">
        <Row>
          <Item label="Default">
            <Tag>NNE</Tag>
          </Item>
          <Item label="Default">
            <Tag>SW</Tag>
          </Item>
          <Item label="Default">
            <Tag>WSW</Tag>
          </Item>
        </Row>
      </Section>

      {/* ------------------------------------------------------------------ */}
      <Section title="DetailPageSpotHeader">
        <DetailPageSpotHeader
          area="Nynäshamn"
          name="Torö"
          idealDirs={["NNE", "E", "ENE", "ESE"]}
        />
      </Section>

      {/* ------------------------------------------------------------------ */}
      <Section title="SpotDescription">
        <SpotDescription
          spotName="Trälhavet"
          note="Good local option for SW without the drive to Torö. Flat water, occasional chop. Rocky obstacle 20–40m off the point and one mid-bay — otherwise clean. Limited parking."
          water="Flat to light chop"
          access="6 car spots max, respect local residents"
        />
      </Section>

      {/* ------------------------------------------------------------------ */}
      <Section title="ListItem">
        <ListItem title="South Sweden" subtitle="Skåne, Blekinge" meta="(70)" />
        <ListItem title="Stockholm" subtitle="Stockholm, Uppsala, Södermanland" meta="(58)" />
        <ListItem title="West Sweden" subtitle="Västra Götaland, Halland" meta="(27)" />
        <ListItem title="No subtitle" />
      </Section>

      {/* ------------------------------------------------------------------ */}
      <Section title="ForecastCol">
        <Row gap={8}>
          {MOCK_DAYS.map((day) => (
            <Item
              key={day.dayOffset}
              label={day.level === "skip" ? "skip" : `day ${day.dayOffset} · ${day.level}`}
            >
              <ForecastCol
                day={day}
                spotId="mock-spot"
                unit={unit}
                idealDirs={MOCK_SPOT.ideal.dirs}
              />
            </Item>
          ))}
        </Row>
      </Section>

      {/* ------------------------------------------------------------------ */}
      <Section title="DaySelector — selectable (detail page)">
        <DaySelector
          days={MOCK_DAYS}
          selectable
          selectedDay={selectedDay}
          onSelectDay={(d) => {
            setSelectedDay(d);
            setScrollTick((t) => t + 1);
          }}
        />
      </Section>

      {/* ------------------------------------------------------------------ */}
      <Section title="DaySelector — display (overview page)">
        <DaySelector days={MOCK_DAYS} skeletonCount={7} />
        <div style={{ marginTop: 16 }}>
          <DaySelector days={undefined} skeletonCount={7} />
          <Label>skeleton</Label>
        </div>
      </Section>

      {/* ------------------------------------------------------------------ */}
      <Section title="HourlyChart — wind-speed palette sweep (1 → 66 kn)">
        <p style={{ fontSize: 12, color: "var(--foreground-muted)", marginBottom: 12 }}>
          3-day simulated ramp from calm (1 kn) through optimal (10–30 kn) up to
          storm (66 kn) so every band of the colour scale shows up in order.
        </p>
        <HourlyChart
          spot={SWEEP_SPOT}
          visibleDays={3}
          unit={unit}
          selectedHourIdx={-1}
          scrollToDayTick={0}
          scrollToDayOffset={0}
          onSelectHour={() => {}}
          onActiveDayChange={() => {}}
        />
      </Section>

      {/* ------------------------------------------------------------------ */}
      <Section title="HourlyChart (bars only)">
        <p style={{ fontSize: 12, color: "var(--foreground-muted)", marginBottom: 12 }}>
          Selected hour idx: {selectedHourIdx} — click a bar to change.
        </p>
        <HourlyChart
          spot={MOCK_SPOT}
          visibleDays={3}
          unit={unit}
          selectedHourIdx={selectedHourIdx}
          scrollToDayTick={scrollTick}
          scrollToDayOffset={selectedDay}
          onSelectHour={(idx) => setSelectedHourIdx(idx)}
          onActiveDayChange={(d) => setSelectedDay(d)}
        />
      </Section>

      {/* ------------------------------------------------------------------ */}
      <Section title="HourlyForecastCard">
        <HourlyForecastCard
          spot={MOCK_SPOT}
          visibleDays={3}
          unit={unit}
          selectedHour={selectedHour}
          selectedHourIdx={selectedHourIdx}
          scrollToDayTick={scrollTick}
          scrollToDayOffset={selectedDay}
          onSelectHour={(idx) => setSelectedHourIdx(idx)}
          onActiveDayChange={(d) => setSelectedDay(d)}
        />
      </Section>

      {/* ------------------------------------------------------------------ */}
      <Section title="SpotCard">
        <SpotCard spot={MOCK_SPOT} unit={unit} />
        <div style={{ marginTop: 16 }}>
          <SkeletonSpotCard skeletonCount={7} />
          <Label>skeleton</Label>
        </div>
      </Section>

      {/* ------------------------------------------------------------------ */}
      <Section title="Footer">
        <Footer />
      </Section>
    </div>
  );
}
